// services/receipts.js
import { supabase } from '../supabase';

export const getReceiptsByFileOpening = async (fileOpeningId, { limit = 10, offset = 0, sortBy = 'id', sortOrder = 'desc', searchTerm = '' }) => {
    let query = supabase
        .from('receipts')
        .select('id, amount, balance_amount, payment_method, date, payment_received, reference_number, fid', { count: 'exact' })
        .eq('fid', fileOpeningId)
        .is('deleted_at', null)
        .order(sortBy, { ascending: sortOrder === 'asc' })
        .range(offset, offset + limit - 1);

    if (searchTerm) {
        query = query.or(`reference_number.ilike.%${searchTerm}%,payment_method.ilike.%${searchTerm}%`);
    }

    const { data: receipts, error, count } = await query;

    if (error) {
        throw new Error(error.message);
    }

    return { receipts, count };
};

export const getReceiptById = async (id) => {
    const { data: receipt, error } = await supabase
        .from('receipts')
        .select('*')
        .eq('id', id)
        .single();

    if (error) {
        throw new Error(error.message);
    }

    return receipt;
};

export const createReceipt = async (data) => {
    const { data: receipt, error } = await supabase
        .from('receipts')
        .insert([data])
        .select();

    if (error) {
        throw new Error(error.message);
    }

    return receipt ? receipt[0] : null;
};

export const updateReceipt = async (id, data) => {
    const { data: receipt, error } = await supabase
        .from('receipts')
        .update(data)
        .eq('id', id)
        .select();

    if (error) {
        throw new Error(error.message);
    }

    return receipt ? receipt[0] : null;
};

export const deleteReceipt = async (id) => {
    const { data, error } = await supabase
        .from('receipts')
        .update({ deleted_at: new Date() })
        .eq('id', id);

    if (error) {
        throw new Error(error.message);
    }

    return data;
};

// NEW FUNCTIONS FOR BALANCE CALCULATION

// Get total amount from all invoices for a file opening
export const getTotalInvoiceAmount = async (fileOpeningId) => {
    const { data: invoices, error } = await supabase
        .from('invoices')
        .select('grand_total')
        .eq('fid', fileOpeningId)
        .is('deleted_at', null);

    if (error) {
        throw new Error(error.message);
    }

    const total = invoices.reduce((sum, invoice) => sum + parseFloat(invoice.grand_total || 0), 0);
    return total;
};

// Get total received amount from all receipts for a file opening
export const getTotalReceivedAmount = async (fileOpeningId) => {
    const { data: receipts, error } = await supabase
        .from('receipts')
        .select('amount')
        .eq('fid', fileOpeningId)
        .eq('payment_received', true)
        .is('deleted_at', null);

    if (error) {
        throw new Error(error.message);
    }

    const total = receipts.reduce((sum, receipt) => sum + parseFloat(receipt.amount || 0), 0);
    return total;
};

// Get balance information for file opening
export const getFileOpeningBalanceInfo = async (fileOpeningId) => {
    const totalInvoices = await getTotalInvoiceAmount(fileOpeningId);
    const totalReceived = await getTotalReceivedAmount(fileOpeningId);
    const balance = totalInvoices - totalReceived;

    return {
        total_invoices: totalInvoices,
        total_received: totalReceived,
        balance_amount: balance,
        fully_paid: balance <= 0
    };
};

// Create receipt with automatic balance calculation
export const createReceiptWithBalance = async (receiptData) => {
    // Calculate current balance before this receipt
    const balanceInfo = await getFileOpeningBalanceInfo(receiptData.fid);

    // Calculate new balance after this receipt
    const paymentAmount = parseFloat(receiptData.amount || 0);
    const newBalance = balanceInfo.balance_amount - (receiptData.payment_received ? paymentAmount : 0);

    const receiptWithBalance = {
        ...receiptData,
        balance_amount: newBalance,
        previous_balance: balanceInfo.balance_amount
    };

    return await createReceipt(receiptWithBalance);
};

// Update receipt with balance recalculation
export const updateReceiptWithBalance = async (receiptId, receiptData) => {
    const currentReceipt = await getReceiptById(receiptId);
    const balanceInfo = await getFileOpeningBalanceInfo(currentReceipt.fid);

    // Calculate balance change
    const currentAmount = parseFloat(currentReceipt.amount || 0);
    const newAmount = parseFloat(receiptData.amount || 0);
    const wasReceived = currentReceipt.payment_received;
    const isReceived = receiptData.payment_received;

    let balanceChange = 0;

    if (wasReceived && !isReceived) {
        // Payment marked as not received, add back to balance
        balanceChange = currentAmount;
    } else if (!wasReceived && isReceived) {
        // Payment marked as received, subtract from balance
        balanceChange = -newAmount;
    } else if (wasReceived && isReceived) {
        // Amount changed on received payment
        balanceChange = currentAmount - newAmount;
    }

    const newBalance = balanceInfo.balance_amount + balanceChange;

    const updatedReceipt = {
        ...receiptData,
        balance_amount: newBalance,
        previous_balance: balanceInfo.balance_amount
    };

    return await updateReceipt(receiptId, updatedReceipt);
};